import React, { useState } from 'react';
import {
    Mail,
    Phone,
    MapPin,
    Facebook,
    Instagram,
    Truck,
    HeadphonesIcon,
    Clock,
    ChevronRight,
    Award,
    Loader2,
    CheckCircle
} from 'lucide-react';
import { config } from '../../config/env';
import TikTokIcon from '../icons/TikTokIcon';

const Footer: React.FC = () => {
    const [email, setEmail] = useState('');
    const [isSubscribing, setIsSubscribing] = useState(false);
    const [subscriptionStatus, setSubscriptionStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    const handleNewsletterSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setErrorMessage('Моля, въведете валиден имейл адрес');
            setSubscriptionStatus('error');
            setTimeout(() => {
                setSubscriptionStatus('idle');
                setErrorMessage('');
            }, 3000);
            return;
        }

        setIsSubscribing(true);
        setSubscriptionStatus('idle');

        try {
            const response = await fetch(`${config.api.baseUrl}/api/newsletter/subscribe`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            if (response.ok) {
                setSubscriptionStatus('success');
                setEmail('');
                setTimeout(() => {
                    setSubscriptionStatus('idle');
                }, 5000);
            } else {
                const data = await response.json();
                setErrorMessage(data.message || 'Възникна грешка. Моля, опитайте отново.');
                setSubscriptionStatus('error');
                setTimeout(() => {
                    setSubscriptionStatus('idle');
                    setErrorMessage('');
                }, 3000);
            }
        } catch (error) {
            setErrorMessage('Възникна грешка. Моля, опитайте отново.');
            setSubscriptionStatus('error');
            setTimeout(() => {
                setSubscriptionStatus('idle');
                setErrorMessage('');
            }, 3000);
        } finally {
            setIsSubscribing(false);
        }
    };

    return (
        <footer className="bg-gray-50 text-gray-700 mt-auto border-t border-gray-200">
            {/* Newsletter Section */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-500 py-8">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col md:flex-row items-center justify-between">
                        <div className="mb-4 md:mb-0">
                            <h3 className="text-white text-xl font-semibold mb-2">
                                Абонирай се за нашия бюлетин
                            </h3>
                            <p className="text-primary-50">
                                Получавайте най-новите оферти и промоции директно в имейла си
                            </p>
                        </div>
                        <form onSubmit={handleNewsletterSubmit} className="flex w-full md:w-auto">
                            <div className="relative flex-1 min-w-0 md:flex-none">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Въведете вашия имейл"
                                    className={`px-4 py-3 w-full md:w-64 rounded-l-lg text-gray-900 focus:outline-none focus:ring-2 ${
                                        subscriptionStatus === 'error'
                                            ? 'focus:ring-red-400 border-2 border-red-400'
                                            : 'focus:ring-primary-400'
                                    }`}
                                    disabled={isSubscribing}
                                />
                                {subscriptionStatus === 'success' && (
                                    <CheckCircle className="absolute right-2 top-3.5 h-5 w-5 text-green-600" />
                                )}
                            </div>
                            <button
                                type="submit"
                                disabled={isSubscribing}
                                className="bg-white hover:bg-gray-100 disabled:bg-gray-200 text-primary-600 disabled:text-gray-400 font-semibold px-4 sm:px-6 py-3 rounded-r-lg transition-colors duration-200 flex items-center flex-shrink-0 whitespace-nowrap"
                            >
                                {isSubscribing ? (
                                    <>
                                        <Loader2 className="animate-spin h-4 w-4 mr-2" />
                                        Изпращане...
                                    </>
                                ) : (
                                    'Абониране'
                                )}
                            </button>
                        </form>
                    </div>
                    {subscriptionStatus === 'success' && (
                        <p className="text-white text-sm mt-2 text-center md:text-right">
                            ✓ Успешно се абонирахте за нашия бюлетин!
                        </p>
                    )}
                    {subscriptionStatus === 'error' && (
                        <p className="text-red-100 text-sm mt-2 text-center md:text-right">
                            {errorMessage}
                        </p>
                    )}
                </div>
            </div>

            {/* Main Footer Content */}
            <div className="container mx-auto px-4 py-12 bg-white">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {/* Company Info */}
                    <div className="lg:col-span-1">
                        <h3 className="text-primary-600 text-lg font-semibold mb-4">PC Center</h3>
                        <p className="text-sm mb-4 text-gray-600">
                            Вашият надежден партньор за компютърна техника и аксесоари
                        </p>
                        <div className="flex space-x-3">
                            <a href="https://www.facebook.com/share/17ZFPvKPi5/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="bg-gray-100 p-2 rounded-lg hover:bg-primary-500 hover:text-white transition-all duration-200">
                                <Facebook className="h-5 w-5" />
                            </a>
                            <a href="https://www.instagram.com/pc_center.bg/" target="_blank" rel="noopener noreferrer" className="bg-gray-100 p-2 rounded-lg hover:bg-primary-500 hover:text-white transition-all duration-200">
                                <Instagram className="h-5 w-5" />
                            </a>
                            <a href="https://www.tiktok.com/@pc_center.bg?_t=ZN-8zqi7N1j2Mo" target="_blank" rel="noopener noreferrer" className="bg-gray-100 p-2 rounded-lg hover:bg-primary-500 hover:text-white transition-all duration-200">
                                <TikTokIcon className="h-5 w-5" />
                            </a>
                        </div>
                    </div>

                    {/* Customer Service */}
                    <div>
                        <h4 className="text-gray-900 text-base font-semibold mb-4">Обслужване на клиенти</h4>
                        <ul className="space-y-2 text-sm">
                            <li>
                                <a href="#" className="text-gray-600 hover:text-primary-600 transition-colors duration-200 flex items-center">
                                    <ChevronRight className="h-3 w-3 mr-1 text-primary-400" />
                                    За нас
                                </a>
                            </li>
                            <li>
                                <a href="/terms" className="text-gray-600 hover:text-primary-600 transition-colors duration-200 flex items-center">
                                    <ChevronRight className="h-3 w-3 mr-1 text-primary-400" />
                                    Условия за ползване
                                </a>
                            </li>
                            <li>
                                <a href="/privacy-policy" className="text-gray-600 hover:text-primary-600 transition-colors duration-200 flex items-center">
                                    <ChevronRight className="h-3 w-3 mr-1 text-primary-400" />
                                    Политика за поверителност
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="text-gray-900 text-base font-semibold mb-4">Къде се намираме</h4>
                        <iframe
                            title="PC Center на картата"
                            src="https://www.google.com/maps?q=PC+Center,+ул.+Княз+Борис+I+179,+1407+София,+България&output=embed"
                            className="w-full h-48 rounded-lg border border-gray-200"
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            allowFullScreen
                        />
                    </div>

                    {/* Contact */}
                    <div>
                        <h4 className="text-gray-900 text-base font-semibold mb-4">Свържете се с нас</h4>
                        <ul className="space-y-3 text-sm">
                            <li className="flex items-start">
                                <Phone className="h-4 w-4 mr-2 mt-0.5 text-primary-500" />
                                <div>
                                    <p className="text-gray-800 font-medium">+359 877 769 575</p>
                                    <p className="text-xs text-gray-500">Пон-Пет: 09:00 - 17:00</p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <Mail className="h-4 w-4 mr-2 mt-0.5 text-primary-500" />
                                <a href="mailto:info@pccenter.bg" className="text-gray-600 hover:text-primary-600 transition-colors duration-200">
                                    info@pccenter.bg
                                </a>
                            </li>
                            <li className="flex items-start">
                                <MapPin className="h-4 w-4 mr-2 mt-0.5 text-primary-500" />
                                <div className="text-gray-600">
                                    <p>ул. „Княз Борис I" 179</p>
                                    <p className="text-xs">1407 София, България</p>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Features Bar */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-12 pt-8 border-t border-gray-200">
                    <div className="flex items-center">
                        <div className="bg-primary-50 p-3 rounded-lg mr-3">
                            <Truck className="h-6 w-6 text-primary-600" />
                        </div>
                        <div>
                            <h5 className="text-gray-900 font-semibold text-sm">Безплатна доставка</h5>
                            <p className="text-xs text-gray-500">За поръчки над €200</p>
                        </div>
                    </div>
                    <div className="flex items-center">
                        <div className="bg-primary-50 p-3 rounded-lg mr-3">
                            <Award className="h-6 w-6 text-primary-600" />
                        </div>
                        <div>
                            <h5 className="text-gray-900 font-semibold text-sm">Качествени продукти</h5>
                            <p className="text-xs text-gray-500">Оригинални марки</p>
                        </div>
                    </div>
                    <div className="flex items-center">
                        <div className="bg-primary-50 p-3 rounded-lg mr-3">
                            <Clock className="h-6 w-6 text-primary-600" />
                        </div>
                        <div>
                            <h5 className="text-gray-900 font-semibold text-sm">Бърза доставка</h5>
                            <p className="text-xs text-gray-500">1-3 работни дни</p>
                        </div>
                    </div>
                    <div className="flex items-center">
                        <div className="bg-primary-50 p-3 rounded-lg mr-3">
                            <HeadphonesIcon className="h-6 w-6 text-primary-600" />
                        </div>
                        <div>
                            <h5 className="text-gray-900 font-semibold text-sm">Експертна поддръжка</h5>
                            <p className="text-xs text-gray-500">Професионални консултации</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="bg-primary-700 py-4">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col md:flex-row justify-between items-center text-xs">
                        <p className="text-white">© {new Date().getFullYear()} PC Center. Всички права запазени.</p>
                        <div className="flex gap-4 mt-2 md:mt-0">
                            <a href="#" className="text-primary-100 hover:text-white transition-colors duration-200">Карта на сайта</a>
                            <a href="#" className="text-primary-100 hover:text-white transition-colors duration-200">Бисквитки</a>
                            <a href="#" className="text-primary-100 hover:text-white transition-colors duration-200">Правни условия</a>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;